import React from "react";
import { Link } from "react-router-dom";
import { Instagram, Mail } from "lucide-react";
import { BRAND_ASSETS } from "@/lib/brandAssets";
import { useSiteContent } from "@/context/SiteContentContext";

export default function PublicFooter() {
  const { t } = useSiteContent();
  const contactEmail = t("footer.contact_email", "heritagebloomclub@gmail.com");
  return (
    <footer className="mt-20" style={{ background: "#2A1432", color: "#EFE5EF" }} data-testid="public-footer">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-20 grid grid-cols-1 md:grid-cols-12 gap-10">
        <div className="md:col-span-5">
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#EFE5EF]/60 mb-4">
            ✦ {t("footer.tagline", "Heritage Bloom Club · Aotearoa New Zealand")} ✦
          </div>
          <div className="flex items-start gap-5">
            <img src={BRAND_ASSETS.logo_circle} alt="Heritage Bloom Club" className="w-20 h-20 object-contain flex-shrink-0" />
            <div>
              <h3 className="font-serif italic text-3xl md:text-4xl leading-[1.05]">
                An heirloom<br/>flower society.
              </h3>
            </div>
          </div>
          <p className="font-serif italic text-lg mt-6 max-w-md" style={{ color: "rgba(239,229,239,0.72)" }}>
            Local flowers. Local farms. A small, considered way to bring the season home. Built quietly, season after season.
          </p>
          <div className="mt-8 flex items-center gap-3">
            <a href="https://heritagedahlias.co.nz" target="_blank" rel="noreferrer" className="text-[10px] tracking-[0.25em] uppercase" style={{ color: "rgba(239,229,239,0.6)" }}>
              ← Heritage Dahlias
            </a>
          </div>
        </div>

        <FooterCol title="The Club" links={[
          { to: "/how-it-works", label: "How it works" },
          { to: "/waitlist", label: "Join the waitlist" },
          { to: "/about", label: "Our manifesto" },
        ]} />

        <FooterCol title="Network" links={[
          { to: "/growers", label: "Meet the growers" },
          { to: "/territories", label: "Territory map" },
          { to: "/become-a-grower", label: "Become a grower" },
          { to: "/partnership/etchery", label: "The Etchery Partnership" },
        ]} />

        <FooterCol title="Sign In" links={[
          { to: "/login", label: "Grower portal" },
          { to: "/register", label: "Begin application" },
        ]} contact={true} contactEmail={contactEmail} />
      </div>

      <div style={{ borderTop: "1px solid rgba(239,229,239,0.12)" }}>
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-6 flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="text-[10px] tracking-[0.25em] uppercase" style={{ color: "rgba(239,229,239,0.45)" }}>
            {t("footer.copyright", "© Heritage Bloom Club. All rights reserved.")}
          </div>
          <div className="flex items-center gap-5">
            <div className="text-[10px] tracking-[0.25em] uppercase" style={{ color: "rgba(239,229,239,0.45)" }}>
              Built with care in Aotearoa
            </div>
            <Link
              to="/login"
              className="text-[9px] tracking-[0.3em] uppercase opacity-40 hover:opacity-90 transition"
              style={{ color: "rgba(239,229,239,0.6)" }}
              data-testid="footer-admin-link"
            >
              · Admin ·
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}

function FooterCol({ title, links, contact, contactEmail }) {
  return (
    <div className="md:col-span-2">
      <div className="text-[10px] tracking-[0.25em] uppercase mb-4" style={{ color: "rgba(239,229,239,0.45)" }}>{title}</div>
      <ul className="space-y-2.5">
        {links.map((l) => (
          <li key={l.to}>
            <Link to={l.to} className="font-serif italic text-base hover:opacity-75 transition" style={{ color: "#EFE5EF" }}>{l.label}</Link>
          </li>
        ))}
        {contact && (
          <li className="pt-4 flex items-center gap-3">
            <a href={`mailto:${contactEmail}`} style={{ color: "rgba(239,229,239,0.6)" }}><Mail size={16} /></a>
            <a href="https://instagram.com/heritagedahlias" target="_blank" rel="noreferrer" style={{ color: "rgba(239,229,239,0.6)" }}><Instagram size={16} /></a>
          </li>
        )}
      </ul>
    </div>
  );
}
